import { useCallback } from 'react';
import { ApolloCache, FetchResult } from '@apollo/client';
import {
  DeleteTimeSignalsMutation,
  TimeSignal,
  TimeSignalsDocument,
  TimeSignalsQuery,
  useDeleteTimeSignalsMutation
} from 'graphql/types';
import { useMeContext } from 'modules/me/MeContextProvider';
import { useNotesToastContext } from '../NotesToastContextProvider';
import {
  NOTES_PAGE_TYPE,
  useNotesFilterContext
} from '../NotesFilterContextProvider';
import { TIMELINE_TIMESIGNAL_COUNT } from '../Timeline';
import { truncateMessage } from '../common/utils';
import { clearCachedDates } from '../cache/utils/time-signal-utils';
import {
  useTimeSignalGetParam,
  TimeSignalGetParam,
  useTimeSignalUpdateFilter
} from './useTimeSignalParam';

export const deleteTimeSignalsCache = (param: TimeSignalGetParam) => (
  cache: ApolloCache<DeleteTimeSignalsMutation>,
  mutationResult: FetchResult<
    DeleteTimeSignalsMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.deleteTimeSignals?.ids) {
    const timeSignalsToRemove = mutationResult?.data?.deleteTimeSignals?.ids;
    const existingSignals: TimeSignalsQuery | null = cache.readQuery({
      query: TimeSignalsDocument,
      variables: param
    });
    if (existingSignals) {
      const updatedSignals = (existingSignals!.timeSignals.items || []).filter(
        (s) => !timeSignalsToRemove.includes(s.id)
      );
      cache.writeQuery({
        query: TimeSignalsDocument,
        variables: param,
        data: {
          timeSignals: {
            __typename: 'TimeSignalsResult',
            items: updatedSignals,
            totalCount:
              existingSignals.timeSignals.totalCount -
              timeSignalsToRemove.length,
            cursor: existingSignals.timeSignals?.cursor
          }
        }
      });
    }
  }
};

type BulkDeleteArgs = {
  selectedNoteIds: Array<string>;
  unSelectedNoteIds: Array<string>;
  note?: TimeSignal;
};

export const getMoveMessage = (note?: TimeSignal): string =>
  note
    ? 'toast.note.classification.singleMove.deleteforever'
    : 'toast.note.classification.bulkMove.deleteforever';

export const useBulkDeleteTimeSignals = (): {
  onTimeSignalsBulkDelete: ({
    selectedNoteIds,
    unSelectedNoteIds
  }: BulkDeleteArgs) => Promise<void>;
  deleteInProgress: boolean;
} => {
  const {
    filterContext: { pageType }
  } = useNotesFilterContext();
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );
  const selectedFilterFn = useTimeSignalUpdateFilter();

  const [
    bulkDeleteTimeSignals,
    { loading, error }
  ] = useDeleteTimeSignalsMutation();

  const { setToastContext } = useNotesToastContext();
  const { id } = useMeContext() || {};

  const onTimeSignalsBulkDelete = useCallback(
    async ({ selectedNoteIds, unSelectedNoteIds, note }: BulkDeleteArgs) => {
      if (!loading && error) {
        setToastContext({
          showError: true,
          message: 'toast.note.failure'
        });
      }

      const selectionFilter = selectedFilterFn({
        includeIds: selectedNoteIds.length ? selectedNoteIds : undefined,
        excludeIds: unSelectedNoteIds.length ? unSelectedNoteIds : undefined
      });

      let result: any = null;
      clearCachedDates(id);

      do {
        result = await bulkDeleteTimeSignals({
          variables: {
            input: {
              selectionFilter,
              cursor: result?.data?.deleteTimeSignals.cursor ?? null
            }
          },
          awaitRefetchQueries: false,
          update: deleteTimeSignalsCache(param),
          refetchQueries: ['timeSignalsCount', 'timeSignalsTotals']
        });
      } while (result?.data?.deleteTimeSignals.cursor);

      if (!loading && !error) {
        setToastContext({
          showSuccess: true,
          message: getMoveMessage(note),
          values: {
            message: truncateMessage(note?.description ?? ''),
            count: `${selectedNoteIds.length}`
          }
        });
      }
    },
    [
      loading,
      error,
      selectedFilterFn,
      setToastContext,
      id,
      bulkDeleteTimeSignals,
      param
    ]
  );

  return { onTimeSignalsBulkDelete, deleteInProgress: loading };
};
